import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useTable } from "react-table"
import { QueryClient } from "@tanstack/react-query"
import moment from "moment"
import { _Api } from '../api/_call';
import TableColumnModal from "../common/modules/tableColumn";
import ConfirmationModel from "../common/modules/confirmationmodel";
import { inventorySelectPrintoutTableHead } from '../common/Tablehead';
import "rc-slider/assets/index.css";
import ProductModal from "../common/modules/ProductModal";
import ProductImageSliderModal from "../common/modules/productImageslider";
import { Pagination } from "../common/Pagination";
import Filtermodal from "../common/modules/Filtermodal";
import { loading, Updateloading } from "../common/helpers/toastify";
import { useTranslation } from 'react-i18next';
import Select from "react-select";
import TableLoader from "../../admin/common/TableLoader";
import BarcodeModal from "../common/modules/BarcodeModal"
import {
  selectedInventoryItemsState,
  countSelectedInventoryItemState,
  currentSelectedInventoryItemsState,
  currentSelectedIdState,
  currentCheckIdState
} from "admin/setup/recoilStateSelector/PrintPreviewState";
import { useRecoilState } from "recoil"
import { pageStatusState, pageHeadingBreadCrumbState } from "admin/setup/recoilStateSelector/profilePageState.js";


// Set to true to enable searching multiple Stock IDs/SKUs as tags.
const ENABLE_INVENTORY_MULTI_TAG_SEARCH = false;
// Set to true to search every field from inventory rows already stored in TanStack Query.
const ENABLE_INVENTORY_SEARCH_ALL_COLUMNS = false;
const INVENTORY_SEARCH_MIN_LENGTH = 2;
const INVENTORY_SEARCH_DEBOUNCE_MS = 300;
const getItemId = (item) => String(item?.id ?? "");
const getStockId = (item) => String(item?.stock_id ?? "");

const getSearchableValues = (value) => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value.flatMap(getSearchableValues);
  if (typeof value === "object") return Object.values(value).flatMap(getSearchableValues);
  return [String(value)];
};

// Column filters, same as My Inventory. Keys match the api/v1/Inventory/my/filter options
// and the filters accepted by api/v1/Inventory/my/list/search ("status" is sent as productStatus).
const COLUMN_FILTER_KEYS = {
  4: "item",
  5: "collection",
  6: "metal",
  7: "stone",
  8: "size",
  9: "stock_id",
  14: "location",
  15: "status",
  100: "last_printed",
};

// "Last Printed" is a page-only column (not part of the shared tbl_my_inventory column
// settings), shown right after Stock ID. Its dates come from the barcode print logs.
const LAST_PRINTED_COLUMN_INDEX = 100;
const LAST_PRINTED_FILTER_KEY = "last_printed";
const LAST_PRINTED_COLUMN = { lable: "Last Printed", sort: 1, status: true, index: LAST_PRINTED_COLUMN_INDEX };
const EMPTY_COLUMN_FILTERS = {
  item: [],
  collection: [],
  metal: [],
  stone: [],
  size: [],
  stock_id: [],
  location: [],
  status: [],
};
const COLUMN_FILTER_MENU_WIDTH = 286;

const toSortDate = (value) => {
  if (!value) return null;
  const parsed = moment(
    value,
    ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY/MM/DD", "YYYY-MM-DD", "DD-MM-YYYY"],
    true
  );
  if (parsed.isValid()) return parsed.valueOf();
  const fallback = moment(value);
  return fallback.isValid() ? fallback.valueOf() : null;
};

const toSortNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isNaN(num) ? null : num;
};

// Client-side sort accessor per column index, on the value each cell displays.
// Sorting applies to the rows of the current page (the list is paginated on the server).
const SORT_ACCESSORS = {
  3: (r) => r?.SKU,
  4: (r) => r?.Item_code,
  5: (r) => r?.Collection_code,
  6: (r) => r?.Metal_code,
  7: (r) => r?.Stone_code,
  8: (r) => r?.Size_name,
  9: (r) => r?.stock_id,
  11: (r) => toSortNumber(r?.po_QTY),
  12: (r) => toSortNumber(r?.price),
  13: (r) => toSortNumber(r?.aging),
  14: (r) => r?.location_name,
  15: (r) => r?.status_type,
  16: (r) => r?.POS_Order,
  17: (r) => r?.customer_name,
  18: (r) => toSortDate(r?.delivery_date),
  19: (r) => toSortNumber(r?.due_days),
  20: (r) => r?.remark,
  // "YYYY-MM-DD" sorts correctly as text.
  [LAST_PRINTED_COLUMN_INDEX]: (r) => r?.last_printed,
};

const sortRows = (rows, sortConfig) => {
  const accessor = SORT_ACCESSORS[sortConfig?.index];
  if (!accessor) return rows;
  const sorted = [...rows].sort((a, b) => {
    const av = accessor(a);
    const bv = accessor(b);
    if (av === undefined || av === null || av === "") return 1;
    if (bv === undefined || bv === null || bv === "") return -1;
    if (typeof av === "number" && typeof bv === "number") return av - bv;
    return String(av).localeCompare(String(bv), undefined, { numeric: true });
  });
  return sortConfig.direction === "asc" ? sorted : sorted.reverse();
};
export const inventoryQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const InventoryTableRow = React.memo(function InventoryTableRow({
  row,
  prepareRow,
  selected,
  setImageid,
  setOneproduct,
  setProductId,
  setCartId,
}) {
  prepareRow(row);
  const item = row.original;

  return (
    <tr {...row.getRowProps()} key={row.id}>
      {row.cells.map((cell) => {
        const columnIndex = Number(cell.column.id.replace("inventory-column-", ""));
        return (
          <td
            {...cell.getCellProps()}
            key={cell.column.id}
            onClick={columnIndex === 2 ? () => setImageid(item?.product_id) : columnIndex === 3 ? () => setOneproduct(item) : columnIndex === 10 ? () => { setProductId(item?.id); setCartId(item?.pos?.cart_id) } : undefined}
            data-bs-toggle={columnIndex === 2 || columnIndex === 3 ? "modal" : undefined}
            data-bs-target={columnIndex === 2 ? "#ProductViewModal" : columnIndex === 3 ? "#ProductInfoModal" : undefined}
          >
            {cell.render("Cell")}
          </td>
        );
      })}
    </tr>
  );
});

const InventorySelectToPrintout = () => {
  const { t } = useTranslation();
  const dataFetched = useRef(false);
  const requestSequence = useRef(0);
  const [inventorylist, setInventorylist] = useState([]);
  const [filterdata, setFilterdata] = useState()
  const [filtername, setFiltername] = useState('');
  const [searchTags, setSearchTags] = useState([]);
  const [appliedSearchValue, setAppliedSearchValue] = useState("");
  const searchInputRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const [limit, setLimit] = useState(100);
  const base_assets = process.env.REACT_APP_BASE_ASSETS_URL;
  const [newcolumndata, setnewcolumndata] = useState(inventorySelectPrintoutTableHead);
  const [selectlist, setSelectlist] = useState([]);
  const [getoneproduct, setOneproduct] = useState();
  const [imageid, setImageid] = useState();
  const [product_id, setProduct_id] = useState()
  const [cart_id, setCart_id] = useState()
  const [getSkipNumbers, setGetSkipNumbers] = useState(0)
  const [datalodaer, setDatsLodaer] = useState(false)
  const [datatotal, setDatatotal] = useState({ amount: 0, qty: 0 })
  const [selectedInventoryItems, setSelectedInventoryItems] = useRecoilState(selectedInventoryItemsState)
  const [currentSelectedInventoryItems, setCurrentSelectedInventoryItems] = useRecoilState(currentSelectedInventoryItemsState)
  const [, setCountSelectedInventoryItem] = useRecoilState(countSelectedInventoryItemState)
  const [, setPageStatus] = useRecoilState(pageStatusState)
  const [, setPageHeadingBreadCrumb] = useRecoilState(pageHeadingBreadCrumbState)
  const [currentSelectedId, setCurrentSelectedId] = useRecoilState(currentSelectedIdState)
  const [, setCurrentCheckId] = useRecoilState(currentCheckIdState)
  const [headingActionsEl, setHeadingActionsEl] = useState(null)
  const [sortConfig, setSortConfig] = useState({ index: null, direction: "asc" })
  const [colFilterOptions, setColFilterOptions] = useState({})
  const [activeFilters, setActiveFilters] = useState(EMPTY_COLUMN_FILTERS)
  const [colFilterSearch, setColFilterSearch] = useState({})
  const [openColFilter, setOpenColFilter] = useState(null)
  const [colFilterPortalStyle, setColFilterPortalStyle] = useState({})
  const colFilterTriggerRefs = useRef({})
  const colFilterMenuRef = useRef(null)
  // { "YYYY-MM-DD": [stock_id, ...] } from the barcode print logs (newest day first).
  const [printedByDate, setPrintedByDate] = useState({})
  // Days ticked in the "Last Printed" filter tree (draft until Apply).
  const [printedDaysDraft, setPrintedDaysDraft] = useState([])
  const [expandedPrintedNodes, setExpandedPrintedNodes] = useState({})

  useLayoutEffect(() => {
    setHeadingActionsEl(document.getElementById("barcode-heading-actions"))
  }, [])

  const selectOptions = [
    { label: 100, value: 100 },
    { label: 200, value: 200 },
    { label: 300, value: 300 }
  ]

  const [state, setstate] = useState({
    totalPages: 0,
    currentPage: 1,
  });
  const { totalPages, currentPage } = state;

  const cachedInventory = useMemo(() => {
    const cachedQueries = inventoryQueryClient.getQueriesData({
      queryKey: ["barcode-printout-inventory"],
    });
    const rowsById = new Map();

    cachedQueries.forEach(([, response]) => {
      (response?.data || []).forEach((item) => rowsById.set(getItemId(item), item));
    });

    return rowsById.size > 0 ? [...rowsById.values()] : inventorylist;
  }, [inventorylist]);

  const allColumnSearchTerms = useMemo(() => {
    const values = Array.isArray(appliedSearchValue) ? appliedSearchValue : [appliedSearchValue];
    return values
      .map((value) => String(value ?? "").trim().toLowerCase())
      .filter(Boolean);
  }, [appliedSearchValue]);

  const allColumnSearchResults = useMemo(() => {
    if (!ENABLE_INVENTORY_SEARCH_ALL_COLUMNS || allColumnSearchTerms.length === 0) {
      return inventorylist;
    }

    return cachedInventory.filter((item) => {
      const searchableText = getSearchableValues(item).join(" ").toLowerCase();
      return allColumnSearchTerms.some((term) => searchableText.includes(term));
    });
  }, [allColumnSearchTerms, cachedInventory, inventorylist]);

  const tableInventory = ENABLE_INVENTORY_SEARCH_ALL_COLUMNS
    ? allColumnSearchResults
    : inventorylist;

  const availableInventory = useMemo(() => {
    const selectedStockIds = new Set((currentSelectedId || []).map((id) => String(id)));
    return (tableInventory || []).filter((item) => !selectedStockIds.has(getStockId(item)));
  }, [currentSelectedId, tableInventory]);

  const selectableInventory = useMemo(
    () => availableInventory.filter((item) => !["reserve", "transit"].includes(item?.status_type)),
    [availableInventory]
  );

  const selectlistSet = useMemo(() => new Set((selectlist || []).map(String)), [selectlist]);
  const pageSelectionState = useMemo(() => {
    const selectedCount = selectableInventory.filter((item) => selectlistSet.has(getItemId(item))).length;
    return {
      selectedCount,
      allSelected: selectableInventory.length > 0 && selectedCount === selectableInventory.length,
      someSelected: selectedCount > 0,
    };
  }, [selectableInventory, selectlistSet]);

  const selectlistRef = useRef(selectlistSet);
  const selectableInventoryRef = useRef(selectableInventory);
  const pageSelectionStateRef = useRef(pageSelectionState);
  selectlistRef.current = selectlistSet;
  selectableInventoryRef.current = selectableInventory;
  pageSelectionStateRef.current = pageSelectionState;

  const getSearchValue = useCallback((searchValue) => {
    if (!ENABLE_INVENTORY_MULTI_TAG_SEARCH) {
      return typeof searchValue === "string" ? searchValue : filtername;
    }

    const values = Array.isArray(searchValue) ? searchValue : searchTags;
    const tags = values
      .map((value) => typeof value === "object" ? value?.value : value)
      .map((value) => String(value ?? "").trim())
      .filter(Boolean);

    return tags.length > 0 ? tags : "";
  }, [filtername, searchTags]);

  const addSearchTags = useCallback((values) => {
    const nextValues = values
      .map((value) => String(value ?? "").trim())
      .filter(Boolean);

    if (nextValues.length === 0) return;

    setSearchTags((previousTags) => {
      const existingValues = new Set(previousTags.map((tag) => tag.value.toLowerCase()));
      const newTags = nextValues
        .filter((value) => !existingValues.has(value.toLowerCase()))
        .map((value) => ({ label: value, value }));
      return [...previousTags, ...newTags];
    });
  }, []);

  const handleSearchInputChange = (event) => {
    if (ENABLE_INVENTORY_MULTI_TAG_SEARCH) {
      const parts = event.currentTarget.value.split(",");
      if (parts.length > 1) {
        addSearchTags(parts.slice(0, -1));
        event.currentTarget.value = parts[parts.length - 1];
      }
    }

    if (!ENABLE_INVENTORY_MULTI_TAG_SEARCH) {
      const searchValue = event.currentTarget.value;
      const trimmedSearchValue = searchValue.trim();
      clearTimeout(searchDebounceRef.current);

      if (trimmedSearchValue.length > 0 && trimmedSearchValue.length < INVENTORY_SEARCH_MIN_LENGTH) {
        return;
      }

      searchDebounceRef.current = setTimeout(() => {
        filterbyname(searchValue);
      }, INVENTORY_SEARCH_DEBOUNCE_MS);
    }
  };

  const handleSearchInputKeyDown = useCallback((event) => {
    const currentValue = event.currentTarget.value;
    if (event.key === "Enter") {
      event.preventDefault();
      addSearchTags([currentValue]);
      event.currentTarget.value = "";
    }

    if (event.key === "Backspace" && !currentValue && searchTags.length > 0) {
      setSearchTags((previousTags) => previousTags.slice(0, -1));
    }
  }, [addSearchTags, searchTags.length]);

  const removeSearchTag = useCallback((tagValue) => {
    setSearchTags((previousTags) => previousTags.filter((tag) => tag.value !== tagValue));
  }, []);

  useEffect(() => () => clearTimeout(searchDebounceRef.current), []);

  const clearCurrentSelection = useCallback(() => {
    setSelectlist([]);
    setCurrentSelectedInventoryItems([]);
    setCurrentCheckId([]);
  }, [setCurrentCheckId, setCurrentSelectedInventoryItems]);

  const handleSelectionChange = useCallback((item, checked) => {
    const itemId = getItemId(item);
    const stockId = item?.stock_id;
    if (!itemId) return;

    if (checked) {
      setSelectlist((prev) => prev.includes(itemId) ? prev : [...prev, itemId]);
      setCurrentCheckId((prev) => prev.some((id) => String(id) === String(stockId)) ? prev : [...prev, stockId]);
      setCurrentSelectedInventoryItems((prev) => {
        const current = Array.isArray(prev) ? prev : [];
        return current.some((selected) => getItemId(selected) === itemId) ? current : [...current, item];
      });
      return;
    }

    setSelectlist((prev) => prev.filter((id) => String(id) !== itemId));
    setCurrentCheckId((prev) => prev.filter((id) => String(id) !== String(stockId)));
    setCurrentSelectedInventoryItems((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      return current.filter((selected) => getItemId(selected) !== itemId);
    });
  }, [setCurrentCheckId, setCurrentSelectedInventoryItems]);

  const handleTogglePageSelection = useCallback((checked) => {
    const pageItems = selectableInventory;
    const pageItemIds = new Set(pageItems.map(getItemId));

    setSelectlist((prev) => {
      const next = new Set(prev || []);
      pageItemIds.forEach((id) => checked ? next.add(id) : next.delete(id));
      return [...next];
    });

    setCurrentSelectedInventoryItems((prev) => {
      const next = new Map((Array.isArray(prev) ? prev : []).map((item) => [getItemId(item), item]));
      pageItems.forEach((item) => {
        if (checked) next.set(getItemId(item), item);
        else next.delete(getItemId(item));
      });
      return [...next.values()];
    });

    setCurrentCheckId((prev) => {
      const next = new Map((prev || []).map((id) => [String(id), id]));
      pageItems.forEach((item) => {
        const key = getStockId(item);
        if (checked) next.set(key, item.stock_id);
        else next.delete(key);
      });
      return [...next.values()];
    });
  }, [selectableInventory, setCurrentCheckId, setCurrentSelectedInventoryItems]);

  const Getmyinventory = useCallback(async (current = 1, firstlimit, filter, search, skip) => {
    const tablelimit = firstlimit || limit;
    const currentPageNumber = current || 1;
    const skipNumber = typeof skip === "number" ? skip : (currentPageNumber - 1) * tablelimit;
    const activeSearch = ENABLE_INVENTORY_SEARCH_ALL_COLUMNS ? "" : getSearchValue(search);
    const requestId = ++requestSequence.current;
    setDatsLodaer(true)

    const postdata = {
      "item": filter?.item ? filter.item : [],
      "collection": filter?.collection ? filter.collection : [],
      "metal": filter?.metal ? filter.metal : [],
      "size": filter?.size ? filter.size : [],
      "stone": filter?.stone ? filter.stone : [],
      "productStatus": filter?.status ? filter.status : [],
      "location": filter?.location ? filter.location : [],
      "stock_id": filter?.stock_id ? filter.stock_id : [],
      // Stock IDs printed on the days ticked in the "Last Printed" filter; excluded on the server before pagination.
      "exclude_stock_ids": filter?.exclude_stock_ids ? filter.exclude_stock_ids : [],
      "price": filter?.price ? filter.price : [],
      "search": activeSearch,
      "limit": tablelimit,
      "skip": skipNumber,
    };

    try {
      const resPoinventory = await inventoryQueryClient.fetchQuery({
        queryKey: ['barcode-printout-inventory', postdata],
        queryFn: () => _Api(postdata, `api/v1/Inventory/my/list/search`),
      });
      if (requestId !== requestSequence.current) return;

      if (resPoinventory?.code === 200) {
        setFilterdata(filter)
        setGetSkipNumbers(skipNumber);
        setstate((prev) => ({
          ...prev,
          totalPages: Math.ceil(resPoinventory?.total / tablelimit),
          currentPage: currentPageNumber
        }));
        setInventorylist(resPoinventory?.data || []);
        // Totals come with the list (same as My Inventory): qty, and price summed per
        // currency, e.g. "$3,500.00+฿6,700.00".
        setDatatotal({
          qty: resPoinventory?.total_qty ?? 0,
          amount: resPoinventory?.total_price ?? "",
        });
      } else {
        setInventorylist([]);
        setDatatotal({ qty: 0, amount: "" });
        setstate((prev) => ({ ...prev, totalPages: 0, currentPage: 1 }));
      }
    } catch (err) {
      if (requestId === requestSequence.current) console.log(err, 'err');
    } finally {
      if (requestId === requestSequence.current) setDatsLodaer(false);
    }
  }, [getSearchValue, limit]);

  useEffect(() => {
    if (dataFetched.current) return;
    dataFetched.current = true;
    Getmyinventory();
  }, [Getmyinventory]);

  useEffect(() => {
    const callcolumns = async () => {
      try {
        const columnRequest = { name: 'tbl_my_inventory' };
        const columnhead = await inventoryQueryClient.fetchQuery({
          queryKey: ['barcode-printout-inventory-columns', columnRequest],
          queryFn: () => _Api(columnRequest, `api/v1/Inventory/common/getColumnHeads`),
        });
        if (columnhead?.code === 200 && Array.isArray(columnhead?.data?.data)) {
          setnewcolumndata(columnhead.data.data);
        }
      } catch (err) {
        console.log(err, "err");
      }
    };
    callcolumns();
  }, []);

  const filterbyname = (searchValue) => {
    const currentSearchValue = searchValue === undefined
      ? (ENABLE_INVENTORY_MULTI_TAG_SEARCH ? searchTags : searchInputRef.current?.value || "")
      : searchValue;
    const appliedSearch = getSearchValue(currentSearchValue);
    const hasShortSearchTerm = Array.isArray(appliedSearch)
      ? appliedSearch.some((value) => String(value ?? "").trim().length === 1)
      : String(appliedSearch ?? "").trim().length === 1;

    if (hasShortSearchTerm) return;

    setstate((prev) => ({ ...prev, currentPage: 1 }));

    if (ENABLE_INVENTORY_SEARCH_ALL_COLUMNS) {
      setAppliedSearchValue(appliedSearch);
      setGetSkipNumbers(0);
      return;
    }

    setFiltername(typeof appliedSearch === "string" ? appliedSearch : "");
    Getmyinventory(1, limit, filterdata, appliedSearch, 0);
  };

  const getlimit = (nextLimit) => {
    setLimit(nextLimit);
    setstate((prev) => ({ ...prev, currentPage: 1 }));
    Getmyinventory(1, nextLimit, filterdata, filtername, 0);
  };

  const handlePaginations = (nextPage) => {
    setstate((prev) => ({ ...prev, currentPage: nextPage }));
    Getmyinventory(nextPage, limit, filterdata, filtername);
  };

  const confirmSelection = () => {
    const pendingItems = Array.isArray(currentSelectedInventoryItems) ? currentSelectedInventoryItems : [];
    const existingItems = Array.isArray(selectedInventoryItems) ? selectedInventoryItems : [];
    const nextItems = [...new Map([...existingItems, ...pendingItems].map((item) => [getItemId(item), item])).values()];
    const nextStockIds = [...new Set([...currentSelectedId, ...pendingItems.map((item) => item.stock_id)])];

    setSelectedInventoryItems(nextItems);
    setCountSelectedInventoryItem(nextItems.length);
    setCurrentSelectedId(nextStockIds);
    setCurrentSelectedInventoryItems([]);
    setCurrentCheckId([]);
    setSelectlist([]);
    setPageStatus("print_preview_page")
    setPageHeadingBreadCrumb({
      heading: "Barcode Printing",
      breadCrumbName: "Barcode Printing",
    })
    window.location.href = "#print_preview_page";
  };

  const deallcocation = async (event) => {
    const GetloadingID = loading();
    try {
      const postdata = {
        "po_product_id": product_id,
        "customOrderId": cart_id,
        "type": event
      }
      const res = await _Api(postdata, `api/v1/Inventory/po/deallocation`);
      if (res.code === 200) {
        Updateloading(t, GetloadingID, t("Deallocation success!!"));
        await Promise.all([
          inventoryQueryClient.invalidateQueries({ queryKey: ['barcode-printout-inventory'] }),
          inventoryQueryClient.invalidateQueries({ queryKey: ['barcode-printout-inventory-lower-count'] }),
        ]);
      } else {
        Updateloading(t, GetloadingID, t("somthing went wrong"), 'error');
      }
      await Getmyinventory();
    } catch (err) {
      console.log(err, 'err');
    }
  }

  useEffect(() => {
    const getColFilterOptions = async () => {
      try {
        const res = await _Api({ custom_filter_enable: true }, `api/v1/Inventory/my/filter`);
        if (res?.code === 200) setColFilterOptions(res?.data || {});
      } catch (err) {
        console.log(err, "err");
      }
    };
    getColFilterOptions();
  }, []);

  // ---- "Last Printed" column + filter ----
  // Days applied to the list (the tree edits printedDaysDraft until Apply).
  const [appliedPrintedDays, setAppliedPrintedDays] = useState([]);

  const loadPrintedStockIds = useCallback(async () => {
    try {
      const res = await _Api({}, `api/v1/barcode/print-log/printed-stock-ids`);
      if (res?.code === 200) setPrintedByDate(res?.data || {});
    } catch (err) {
      console.log(err, "err");
    }
  }, []);

  useEffect(() => {
    loadPrintedStockIds();
  }, [loadPrintedStockIds]);

  // stock_id -> latest "YYYY-MM-DD" it was printed on.
  const lastPrintedByStock = useMemo(() => {
    const map = new Map();
    Object.keys(printedByDate || {})
      .sort((a, b) => b.localeCompare(a))
      .forEach((day) => {
        (printedByDate[day] || []).forEach((stockId) => {
          const key = String(stockId);
          if (!map.has(key)) map.set(key, day);
        });
      });
    return map;
  }, [printedByDate]);

  // Year -> month -> day tree, newest first, with unique stock-id counts at every level.
  const printedTree = useMemo(() => {
    const years = new Map();
    Object.keys(printedByDate || {}).forEach((day) => {
      const [year, month] = day.split("-");
      if (!years.has(year)) years.set(year, new Map());
      const months = years.get(year);
      const monthKey = `${year}-${month}`;
      if (!months.has(monthKey)) months.set(monthKey, []);
      months.get(monthKey).push(day);
    });
    const uniqueCount = (days) => new Set(days.flatMap((day) => printedByDate[day] || [])).size;
    return [...years.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([year, months]) => {
        const monthList = [...months.entries()]
          .sort(([a], [b]) => b.localeCompare(a))
          .map(([monthKey, days]) => {
            const sortedDays = [...days].sort((a, b) => b.localeCompare(a));
            return { key: monthKey, label: moment(monthKey, "YYYY-MM").format("MMMM"), days: sortedDays, count: uniqueCount(sortedDays) };
          });
        const yearDays = monthList.flatMap((month) => month.days);
        return { key: year, label: year, months: monthList, days: yearDays, count: uniqueCount(yearDays) };
      });
  }, [printedByDate]);

  const getExcludedStockIds = useCallback(
    (days) => [...new Set(days.flatMap((day) => printedByDate[day] || []).map(String))],
    [printedByDate]
  );

  const openPrintedFilter = () => {
    // Refresh so prints made since the page loaded show up in the tree.
    loadPrintedStockIds();
    setPrintedDaysDraft(appliedPrintedDays);
    // Tree starts fully collapsed (years only) every time the menu opens.
    setExpandedPrintedNodes({});
  };

  // The table header is memoized; it calls this through a ref so it always sees current state.
  const openPrintedFilterRef = useRef(openPrintedFilter);
  openPrintedFilterRef.current = openPrintedFilter;

  const togglePrintedDays = (days, checked) => {
    setPrintedDaysDraft((prev) => {
      const next = new Set(prev);
      days.forEach((day) => (checked ? next.add(day) : next.delete(day)));
      return [...next];
    });
  };

  const togglePrintedNode = (key) => {
    setExpandedPrintedNodes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getColFilterPortalStyle = useCallback((filterKey) => {
    const trigger = colFilterTriggerRefs.current[filterKey];
    if (!trigger) return {};
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    let left = rect.left;
    if (left + COLUMN_FILTER_MENU_WIDTH + 8 > viewportWidth) {
      left = Math.max(8, viewportWidth - COLUMN_FILTER_MENU_WIDTH - 8);
    }
    return { position: "fixed", top: rect.bottom + 6, left, width: COLUMN_FILTER_MENU_WIDTH, zIndex: 3000 };
  }, []);

  // Keep the open filter menu attached to its header icon (on resize and on any scroll,
  // including the table's own scroll); close it on outside click.
  useEffect(() => {
    if (!openColFilter) return undefined;
    const handleReposition = (event) => {
      // Scrolling inside the menu's own option list: the menu itself doesn't move.
      if (event?.target instanceof Node && colFilterMenuRef.current?.contains(event.target)) return;
      setColFilterPortalStyle(getColFilterPortalStyle(openColFilter));
    };
    const handleOutsideClick = (event) => {
      const trigger = colFilterTriggerRefs.current[openColFilter];
      if (trigger?.contains(event.target) || colFilterMenuRef.current?.contains(event.target)) return;
      setOpenColFilter(null);
    };
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [getColFilterPortalStyle, openColFilter]);

  const toggleColFilter = useCallback((filterKey) => {
    setOpenColFilter((prev) => {
      const next = prev === filterKey ? null : filterKey;
      if (next) setColFilterPortalStyle(getColFilterPortalStyle(next));
      return next;
    });
  }, [getColFilterPortalStyle]);

  const getColFilterValue = (filterKey, option) => (filterKey === "status" ? option?.value : option?._id);

  const handleColFilterItemChange = (checked, value, filterKey) => {
    setActiveFilters((prev) => ({
      ...prev,
      [filterKey]: checked
        ? [...(prev[filterKey] || []), value]
        : (prev[filterKey] || []).filter((v) => v !== value),
    }));
  };

  const handleColFilterSelectAll = (checked, filterKey) => {
    const allValues = (colFilterOptions?.[filterKey] || []).map((option) => getColFilterValue(filterKey, option));
    setActiveFilters((prev) => ({ ...prev, [filterKey]: checked ? allValues : [] }));
  };

  // Column filters are combined with the filter modal's filters (price etc.) and reload page 1.
  const applyColFilters = (nextFilters) => {
    setOpenColFilter(null);
    setstate((prev) => ({ ...prev, currentPage: 1 }));
    Getmyinventory(1, limit, { ...(filterdata || {}), ...nextFilters }, filtername, 0);
  };

  const handleColFilterSort = (index, direction) => {
    setSortConfig({ index, direction });
    setOpenColFilter(null);
  };

  const handleHeaderSort = useCallback((index) => {
    setSortConfig((prev) =>
      prev.index === index
        ? { index, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { index, direction: "asc" }
    );
  }, []);

  // Each row gets its last printed day ("YYYY-MM-DD") for the Last Printed column and its sort.
  const sortedInventory = useMemo(() => {
    const rows = availableInventory.map((item) => ({
      ...item,
      last_printed: lastPrintedByStock.get(String(item?.stock_id ?? "")) || "",
    }));
    return sortRows(rows, sortConfig);
  }, [availableInventory, lastPrintedByStock, sortConfig]);

  const applyPrintedFilter = (days) => {
    setAppliedPrintedDays(days);
    applyColFilters({ ...activeFilters, exclude_stock_ids: getExcludedStockIds(days) });
  };

  // Year -> Month -> Day tree: ticked days are hidden from the list.
  const renderPrintedFilterMenu = () => {
    const draftSet = new Set(printedDaysDraft);
    const allDays = Object.keys(printedByDate || {});
    const hiddenCount = getExcludedStockIds(printedDaysDraft).length;

    const renderTreeCheckbox = (id, days, label, count, depth, toggleKey) => {
      const selected = days.filter((day) => draftSet.has(day)).length;
      const checked = days.length > 0 && selected === days.length;
      return (
        <li
          className="com-custom-table-tag-filter-menu-select-list-single d-flex align-items-center"
          style={{ paddingLeft: depth * 18 }}
          key={id}
        >
          {toggleKey ? (
            <button
              type="button"
              className="bg-transparent border-0 p-0 me-1"
              style={{ width: 16 }}
              onClick={() => togglePrintedNode(toggleKey)}
              aria-label={expandedPrintedNodes[toggleKey] ? "Collapse" : "Expand"}
            >
              {expandedPrintedNodes[toggleKey] ? "▾" : "▸"}
            </button>
          ) : (
            <span className="me-1" style={{ width: 16, display: "inline-block" }} />
          )}
          <input
            className="fill-com-check form-check-input d-inline-block me-2 cursor-pointer"
            type="checkbox"
            id={id}
            checked={checked}
            ref={(el) => {
              if (el) el.indeterminate = selected > 0 && !checked;
            }}
            onChange={(e) => togglePrintedDays(days, e.target.checked)}
          />
          <label className="form-check-label cursor-pointer flex-grow-1" htmlFor={id}>
            {label}
          </label>
          <span className="ms-2" style={{ color: "#A0A0A0" }}>({count})</span>
        </li>
      );
    };

    return createPortal(
      <div
        className="dropdown-menu show com-custom-table-tag-filter-menu dtbs-filter-dropdown-menu dtbs-filter-portal-menu p-0 border-0 overflow-hidden"
        style={colFilterPortalStyle}
        ref={colFilterMenuRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="asc-des-order">
          <div className="asc-des-order-single cursor-pointer" onClick={() => handleColFilterSort(LAST_PRINTED_COLUMN_INDEX, "asc")}>
            <img src={base_assets + "images/icons/arrow-sort-up.png"} alt="" />
            {t("Sort Ascending")}
          </div>
          <div className="asc-des-order-single cursor-pointer" onClick={() => handleColFilterSort(LAST_PRINTED_COLUMN_INDEX, "desc")}>
            <img src={base_assets + "images/icons/arrow-sort-down.png"} alt="" />
            {t("Sort Descending")}
          </div>
        </div>
        <div className="com-custom-table-tag-filter-menu-select">
          <div className="com-custom-table-tag-filter-menu-select-bar d-flex align-items-center justify-content-between">
            <div>
              <input
                className="fill-com-check form-check-input d-inline-block me-2 cursor-pointer"
                type="checkbox"
                id="printout-colfilter-printed-all"
                checked={allDays.length > 0 && printedDaysDraft.length === allDays.length}
                onChange={(e) => setPrintedDaysDraft(e.target.checked ? allDays : [])}
              />
              <label className="form-check-label cursor-pointer fw-semibold" htmlFor="printout-colfilter-printed-all">
                {t("Hide printed on")}
              </label>
            </div>
            <label>
              {hiddenCount} {t("Hidden")}
            </label>
          </div>
          <ul className="com-custom-table-tag-filter-menu-select-list list-unstyled mt-3 ps-2">
            {printedTree.length ? printedTree.map((year) => (
              <React.Fragment key={year.key}>
                {renderTreeCheckbox(`printed-year-${year.key}`, year.days, year.label, year.count, 0, year.key)}
                {expandedPrintedNodes[year.key] ? year.months.map((month) => (
                  <React.Fragment key={month.key}>
                    {renderTreeCheckbox(`printed-month-${month.key}`, month.days, t(month.label), month.count, 1, month.key)}
                    {expandedPrintedNodes[month.key] ? month.days.map((day) =>
                      renderTreeCheckbox(
                        `printed-day-${day}`,
                        [day],
                        moment(day, "YYYY-MM-DD").format("DD"),
                        (printedByDate[day] || []).length,
                        2,
                        null
                      )
                    ) : null}
                  </React.Fragment>
                )) : null}
              </React.Fragment>
            )) : (
              <li className="text-center" style={{ color: "#A0A0A0" }}>{t("No print history")}</li>
            )}
          </ul>
        </div>
        <div className="com-custom-table-tag-filter-menu-btn-grp text-center d-flex align-items-center gap-2">
          <button
            className="com-custom-table-tag-filter-menu-btn-grp-single flex-grow-1 bg-transparent fw-semibold graythemecolor"
            onClick={() => {
              setPrintedDaysDraft([]);
              applyPrintedFilter([]);
            }}
          >
            {t("Clear")}
          </button>
          <button
            className="com-custom-table-tag-filter-menu-btn-grp-single flex-grow-1 main-green-bg text-white fw-semibold"
            onClick={() => applyPrintedFilter(printedDaysDraft)}
          >
            {t("Apply")}
          </button>
        </div>
      </div>,
      document.body
    );
  };

  const renderColFilterMenu = () => {
    const filterKey = openColFilter;
    if (!filterKey) return null;
    if (filterKey === LAST_PRINTED_FILTER_KEY) return renderPrintedFilterMenu();
    const isStatus = filterKey === "status";
    const sortIndex = Number(Object.keys(COLUMN_FILTER_KEYS).find((index) => COLUMN_FILTER_KEYS[index] === filterKey));
    const options = colFilterOptions?.[filterKey] || [];
    const checkedVals = activeFilters[filterKey] || [];
    const searchTerm = colFilterSearch[filterKey] || "";
    const filteredOptions = searchTerm
      ? options.filter((option) => option?.name?.toLowerCase().includes(searchTerm.toLowerCase()))
      : options;

    return createPortal(
      <div
        className="dropdown-menu show com-custom-table-tag-filter-menu dtbs-filter-dropdown-menu dtbs-filter-portal-menu p-0 border-0 overflow-hidden"
        style={colFilterPortalStyle}
        ref={colFilterMenuRef}
        onClick={(e) => e.stopPropagation()}
      >
        {SORT_ACCESSORS[sortIndex] ? (
          <div className="asc-des-order">
            <div className="asc-des-order-single cursor-pointer" onClick={() => handleColFilterSort(sortIndex, "asc")}>
              <img src={base_assets + "images/icons/arrow-sort-up.png"} alt="" />
              {t("Sort Ascending")}
            </div>
            <div className="asc-des-order-single cursor-pointer" onClick={() => handleColFilterSort(sortIndex, "desc")}>
              <img src={base_assets + "images/icons/arrow-sort-down.png"} alt="" />
              {t("Sort Descending")}
            </div>
          </div>
        ) : null}
        {!isStatus && (
          <div className="com-custom-table-tag-filter-menu-search">
            <div className="com-custom-table-tag-filter-menu-search-inner d-flex align-items-center">
              <img src={base_assets + "images/icons/search-icon.png"} alt="" />
              <input
                className="border-0"
                type="text"
                placeholder={t("Search")}
                value={searchTerm}
                onChange={(e) => setColFilterSearch((prev) => ({ ...prev, [filterKey]: e.target.value }))}
              />
            </div>
          </div>
        )}
        <div className="com-custom-table-tag-filter-menu-select">
          <div className="com-custom-table-tag-filter-menu-select-bar d-flex align-items-center justify-content-between">
            <div>
              <input
                className="fill-com-check form-check-input d-inline-block me-2 cursor-pointer"
                type="checkbox"
                id={`printout-colfilter-all-${filterKey}`}
                onChange={(e) => handleColFilterSelectAll(e.target.checked, filterKey)}
                checked={options.length > 0 && checkedVals.length === options.length}
              />
              <label className="form-check-label cursor-pointer fw-semibold" htmlFor={`printout-colfilter-all-${filterKey}`}>
                {t("Select All")}
              </label>
            </div>
            <label>
              {checkedVals.length}/{options.length} {t("Selected")}
            </label>
          </div>
          <ul className="com-custom-table-tag-filter-menu-select-list list-unstyled mt-3 ps-4">
            {filteredOptions.map((option, idx) => {
              const value = getColFilterValue(filterKey, option);
              const optionId = `printout-colfilter-${filterKey}-${idx}`;
              return (
                <li className="com-custom-table-tag-filter-menu-select-list-single" key={optionId}>
                  <input
                    className="fill-com-check form-check-input d-inline-block me-2 cursor-pointer"
                    type="checkbox"
                    id={optionId}
                    checked={checkedVals.includes(value)}
                    onChange={(e) => handleColFilterItemChange(e.target.checked, value, filterKey)}
                  />
                  <label className="form-check-label cursor-pointer" htmlFor={optionId}>
                    {option?.name}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="com-custom-table-tag-filter-menu-btn-grp text-center d-flex align-items-center gap-2">
          <button
            className="com-custom-table-tag-filter-menu-btn-grp-single flex-grow-1 bg-transparent fw-semibold graythemecolor"
            onClick={() => {
              const nextFilters = { ...activeFilters, [filterKey]: [] };
              setActiveFilters(nextFilters);
              setColFilterSearch((prev) => ({ ...prev, [filterKey]: "" }));
              applyColFilters(nextFilters);
            }}
          >
            {t("Clear")}
          </button>
          <button
            className="com-custom-table-tag-filter-menu-btn-grp-single flex-grow-1 main-green-bg text-white fw-semibold"
            onClick={() => applyColFilters(activeFilters)}
          >
            {t("Apply")}
          </button>
        </div>
      </div>,
      document.body
    );
  };

  const columnConfig = useMemo(
    () => Array.isArray(newcolumndata) && newcolumndata.length ? newcolumndata : inventorySelectPrintoutTableHead,
    [newcolumndata]
  );

  const visibleColumnConfig = useMemo(() => {
    const visible = columnConfig
      .map((config, index) => ({ ...config, index }))
      .filter(({ status, index }) => index === 0 || status !== false);
    // Insert the page-only "Last Printed" column right after Stock ID (or at the end if Stock ID is hidden).
    const stockIdPosition = visible.findIndex(({ index }) => index === 9);
    const insertAt = stockIdPosition >= 0 ? stockIdPosition + 1 : visible.length;
    return [...visible.slice(0, insertAt), LAST_PRINTED_COLUMN, ...visible.slice(insertAt)];
  }, [columnConfig]);

  const renderStockId = useCallback((item) => {
    const stockId = item?.stock_id || "";
    const tooltip = item?.delivery_date;
    const stockClass = {
      stock: "light-blue-text",
      transit: "status-green-text",
      warehouse: "status-gray-text",
      reserve: "yellow-text",
      repair_order: "purple-text",
      custom_order: "danger-text",
    }[item?.status_stock_id];

    return (
      <div className={stockClass ? `${stockClass} col-highlight` : undefined}>
        <div className='tooltip-area-up'>
          {stockId}
          <span className="tooltip-area-up-text">
            {item?.status_stock_id === "reserve" || item?.status_stock_id === "repair_order" || item?.status_stock_id === "custom_order"
              ? <>{item?.customer_name}{item?.customer_name ? <br /> : ''}{item?.POS_Order}<br />{tooltip}</>
              : tooltip}
          </span>
        </div>
      </div>
    );
  }, []);

  const renderAllocationCell = useCallback((item) => (
    <>
      {item?.allocatable === true ?
        <div className="td-icons-wrap m-3 d-inline-block">
          <div className="td-icons-wrap-link">
            <img className="img" src={base_assets + 'images/icons/link-icon.svg'} alt="" />
          </div>
        </div> : ""}
      <div className="td-icons-wrap-user d-inline-block">
        {item?.pos_Qty !== 0 && item?.pos?.order_type === "repair_order" ?
          <div className='tooltip-area-up'>
            <div className="td-icons-wrap-user d-inline-block"><img className="img" src={base_assets + 'images/icons/user-purple.png'} alt="" /></div>
            <span className="tooltip-area-up-text">{item?.customer_name}{item?.customer_name ? <br /> : ''}{item?.POS_Order}<br />{item?.delivery_date}</span>
          </div> : item?.pos_Qty !== 0 ?
            <div className='tooltip-area-up' data-bs-toggle="modal" data-bs-target="#allocation-listedModal">
              <div className="td-icons-wrap-user d-inline-block"><img className="img" src={base_assets + 'images/icons/user-red.png'} alt="" /></div>
              <span className="tooltip-area-up-text">{item?.customer_name}{item?.customer_name ? <br /> : ''}{item?.POS_Order}<br />{item?.delivery_date}</span>
            </div> : ""}
      </div>
      <div className="td-icons-wrap-user d-inline-block">
        <div className='tooltip-area-up'>
          {item?.status_type === "  " ?
            <div className="td-icons-wrap-user d-inline-block"><img className="img" src={base_assets + 'images/icons/user-yellow.png'} alt="" /></div> : ""}
          <span className="tooltip-area-up-text">{item?.customer_name}{item?.customer_name ? <br /> : ''}<br />{item?.delivery_date}</span>
        </div>
      </div>
    </>
  ), [base_assets]);

  const renderStatusCell = useCallback((item) => (
    <div className="status-dot-wrap">
      {item?.status_type === "stock" ?
        <div className="status-dot light-blue-bg tooltip-area-up"><span className='tooltip-area-up-text'>{t("Stock")}</span></div> :
        item?.status_type === "transit" ?
          <div className="status-dot green-bg tooltip-area-up"><span className='tooltip-area-up-text'>{t("Transit")}</span></div> :
          item?.status_type === "warehouse" ?
            <div className="status-dot status-gray-bg tooltip-area-up"><span className='tooltip-area-up-text'>{t("Warehouse")}</span></div> :
            item?.status_type === "reserve" ?
              <div className="status-dot yellow-bg tooltip-area-up"><span className='tooltip-area-up-text'>{t("Reserve")}</span></div> :
              item?.status_type === "repair_order" ?
                <div className="status-dot purple-bg tooltip-area-up"><span className='tooltip-area-up-text'>{t("Repair Order")}</span></div> :
                <div className="status-dot danger-bg tooltip-area-up"><span className='tooltip-area-up-text'>{t("Custom Order")}</span></div>}
    </div>
  ), [t]);

  const renderCellContent = useCallback((columnIndex, item, rowIndex) => {
    switch (columnIndex) {
      case 1:
        return parseInt(rowIndex + getSkipNumbers + 1);
      case 2:
        return (
          <img
            src={item?.main_image ? item.main_image : base_assets + "images/icon/camera_profile.png"}
            alt=""
            style={{ width: "40px", height: "34px", objectFit: "contain", display: "block", margin: "0 auto" }}
          />
        );
      case 3:
        return item?.SKU || "";
      case 4:
        return <div className="tooltip-area-up">{item?.Item_code || ""}<span className="tooltip-area-up-text">{item?.Item_name || ""}</span></div>;
      case 5:
        return <div className="tooltip-area-up">{item?.Collection_code || ""}<span className="tooltip-area-up-text">{item?.Collection_name || ""}</span></div>;
      case 6:
        return <div className="tooltip-area-up">{item?.Metal_code || ""}<span className="tooltip-area-up-text">{item?.Metal_name || ""}</span></div>;
      case 7:
        return <div className="tooltip-area-up">{item?.Stone_code || ""}<span className="tooltip-area-up-text">{item?.Stone_name || ""}</span></div>;
      case 8:
        return item?.Size_name || "";
      case 9:
        return renderStockId(item);
      case 10:
        return renderAllocationCell(item);
      case 11:
        return item?.po_QTY || 0;
      case 12:
        return item?.price || "";
      case 13:
        return item?.aging || "0";
      case 14:
        return <><span className='link-color'>{item?.location_name || ""}</span><span className='text-center location-td-sub'>{item?.warehouse_location_name || ''}</span></>;
      case 15:
        return renderStatusCell(item);
      case 16:
        return item?.POS_Order || "";
      case 17:
        return item?.customer_name || "";
      case 18:
        return item?.delivery_date || "";
      case 19:
        return item?.due_days || "";
      case 20:
        return item?.remark || "-";
      case LAST_PRINTED_COLUMN_INDEX:
        return item?.last_printed ? moment(item.last_printed, "YYYY-MM-DD").format("DD/MM/YYYY") : "-";
      default:
        return "";
    }
  }, [base_assets, getSkipNumbers, renderAllocationCell, renderStatusCell, renderStockId]);

  const tableColumns = useMemo(() => visibleColumnConfig.map(({ index, ...config }) => ({
    id: `inventory-column-${index}`,
    Header: index === 0 ? () => (
      <div className="com-check">
        <input
          id="inventory-select-all"
          type="checkbox"
          checked={pageSelectionStateRef.current.allSelected}
          ref={(node) => {
            if (node) node.indeterminate = pageSelectionStateRef.current.someSelected && !pageSelectionStateRef.current.allSelected;
          }}
          disabled={selectableInventoryRef.current.length === 0}
          onChange={(event) => handleTogglePageSelection(event.target.checked)}
        />
        <label htmlFor="inventory-select-all" className="com-check-label" />
      </div>
    ) : (() => {
      // No sort arrows (same as My Inventory). Filterable columns sort from their filter
      // menu; the other sortable columns still sort by clicking the label.
      const sortable = Boolean(config.sort) && Boolean(SORT_ACCESSORS[index]);
      const filterKey = COLUMN_FILTER_KEYS[index];
      return (
        <>
          <span
            onClick={sortable ? () => handleHeaderSort(index) : undefined}
            style={sortable ? { cursor: "pointer" } : undefined}
          >
            {config.image ? <img src={base_assets + config.image} alt="" /> : t(config.lable)}
          </span>
          {filterKey ? (
            <div
              className="d-inline-block com-custom-table-tag-filter dtbs-filter-dropdown"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                ref={(el) => {
                  colFilterTriggerRefs.current[filterKey] = el;
                }}
                className="after-none bg-transparent border-0 p-0"
                type="button"
                aria-expanded={openColFilter === filterKey}
                onClick={() => {
                  if (filterKey === LAST_PRINTED_FILTER_KEY && openColFilter !== filterKey) {
                    openPrintedFilterRef.current();
                  }
                  toggleColFilter(filterKey);
                }}
              >
                <img
                  className="ms-2 com-custom-table-tag-filter-img"
                  src={base_assets + "images/icons/table-filter-icon.png"}
                  alt=""
                />
              </button>
            </div>
          ) : null}
        </>
      );
    })(),
    Cell: ({ row }) => index === 0 ? (
      <div className={row.original?.status_type === "reserve" || row.original?.status_type === "transit" ? 'com-check checkbox-disabled' : 'com-check'}>
        <input
          className='inventorytablecheck inventorytableHead gisClassTh'
          id={row.original?.id}
          data-id={row.original?.id}
          value={row.original?.id}
          data-status={row.original?.status_type}
          type="checkbox"
          checked={selectlistRef.current.has(getItemId(row.original))}
          disabled={row.original?.status_type === "reserve" || row.original?.status_type === "transit"}
          onChange={(event) => handleSelectionChange(row.original, event.target.checked)}
        />
        <label htmlFor={row.original?.id} className="com-check-label" />
      </div>
    ) : renderCellContent(index, row.original, row.index),
    getHeaderProps: () => ({ className: config.class || "" }),
    getCellProps: () => ({
      className: [
        config.class,
        index === 2 ? "table-pro-img" : "",
        index === 3 ? "col-highlight link-color cursor-pointer" : "",
        [7, 8, 9, 11, 12, 13, 15].includes(index) ? "col-highlight" : "",
        index === 10 ? "sku-col table_btn_expand_col col-highlight" : "",
        index === 14 ? "location-td col-highlight" : "",
        index >= 16 && index <= 19 ? "text-danger col-highlight" : "",
        index === 20 ? "col-highlight" : "",
      ].filter(Boolean).join(" "),
      style: index === 2 ? { width: "60px", minWidth: "60px", height: "50px", padding: 0 } : undefined,
    }),
  })), [base_assets, handleHeaderSort, handleSelectionChange, handleTogglePageSelection, openColFilter, renderCellContent, t, toggleColFilter, visibleColumnConfig]);

  const tableInstance = useTable({
    columns: tableColumns,
    data: sortedInventory,
    getRowId: (row) => getItemId(row),
    autoResetSortBy: false,
    autoResetFilters: false,
  });
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  const confirmButton = (
    <button className="confirm_add_to_print" onClick={confirmSelection}>Confirm</button>
  );

  return (
    <React.Fragment>
      {headingActionsEl ? createPortal(confirmButton, headingActionsEl) : confirmButton}
      <div className="main-body my-inventory-page inventory_select_printout_page" >
        <div className="container">
          <div className="row inventory_select_printout_row">
            <div className="col-lg-12 inventory_select_printout_content">
              <div className="main-body-top-status-bar">
                <Select
                  className="limit-select short mx-3"
                  options={selectOptions}
                  value={{ label: limit, value: limit }}
                  onChange={(event) => getlimit(event.value)}
                />
                  <div className="search_bar_block">
                    <div className="main-body-top-status-bar-filter">
                      <button className="main-body-top-status-bar-filter-icon" data-bs-toggle="modal" data-bs-target="#filterinventory">
                        <img className='d-block' src={base_assets + "/images/icons/search-outline.png"} alt="" />
                      </button>
                    {ENABLE_INVENTORY_MULTI_TAG_SEARCH ? (
                      <div className="inventory-multi-tag-search">
                        {searchTags.map((tag) => (
                          <span className="inventory-search-tag" key={tag.value}>
                            {tag.label}
                            <button
                              type="button"
                              className="inventory-search-tag-remove"
                              aria-label={`Remove ${tag.label}`}
                              onClick={() => removeSearchTag(tag.value)}
                            >
                              x
                            </button>
                          </span>
                        ))}
                        <input
                          type="text"
                          ref={searchInputRef}
                          className="main-body-top-status-bar-filter-input inventory-multi-tag-search-input"
                          placeholder={searchTags.length > 0 ? "" : t("Stock_ID / SKU")}
                          onKeyDown={handleSearchInputKeyDown}
                          onChange={handleSearchInputChange}
                        />
                      </div>
                    ) : (
                      <input
                        type="text"
                        ref={searchInputRef}
                        defaultValue={filtername}
                        className="main-body-top-status-bar-filter-input"
                        placeholder={t("Stock_ID / SKU")}
                        onChange={handleSearchInputChange}
                        onKeyDown={(event) => event.key === 'Enter' && filterbyname(event.currentTarget.value)}
                      />
                    )}
                    {((ENABLE_INVENTORY_MULTI_TAG_SEARCH && searchTags.length > 0) || (!ENABLE_INVENTORY_MULTI_TAG_SEARCH && filtername)) ? <img className="main-body-top-status-bar-filter-clear" src={base_assets + 'images/icons/False.png'} onClick={() => { if (searchInputRef.current) searchInputRef.current.value = ""; setFiltername(""); setSearchTags([]); filterbyname(ENABLE_INVENTORY_MULTI_TAG_SEARCH ? [] : "") }} alt="" /> : ""}
                    <button onClick={() => filterbyname()} className="main-body-top-status-bar-filter-search">
                      <i className="fa fa-search" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div className="main-body-top-status-bar-multi-date-picker filter_date_search_select_inventory" />
                </div>
                {selectlist.length > 0 && (
                  <div className="inventory-selection-summary">
                    <span>{selectlist.length} Add Inventory Selected</span>
                    <button type="button" onClick={clearCurrentSelection} aria-label="Clear selected inventory">x</button>
                  </div>
                )}
                {/* Fill the screen height (leaving room for header, toolbar and the Back/pagination row), never shorter than 440px. */}
                <div className="main-body-main-table-wrap position-relative" style={{ maxWidth: '100%', height: 'calc(100vh - 400px)', minHeight: '440px', overflowX: 'auto', overflowY: 'auto' }}>
                <table {...getTableProps({ id: "my_inventory", className: "stripe row-border order-column common-table table-striped main-table w-100" })}>
                  <thead>
                    {headerGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.getHeaderGroupProps().key}>
                        {headerGroup.headers.map((column) => (
                          <th
                            {...column.getHeaderProps()}
                            key={column.id}
                            // Sticky inside the scrolling table area, so the header and its column
                            // filter icons (and their open menus) stay in view while the rows scroll.
                            style={{ position: "sticky", top: 0, zIndex: 2, background: "#fff" }}
                          >
                            {column.render("Header")}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {rows.length ? rows.map((row) => {
                      const item = row.original;
                      return (
                        <InventoryTableRow
                          key={row.id}
                          row={row}
                          prepareRow={prepareRow}
                          selected={selectlistSet.has(getItemId(item))}
                          setImageid={setImageid}
                          setOneproduct={setOneproduct}
                          setProductId={setProduct_id}
                          setCartId={setCart_id}
                        />
                      );
                    }) : (
                      <tr>
                        <td colSpan={visibleColumnConfig.length} className="text-center">{t("No Data Available In Table")}</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      {visibleColumnConfig.map(({ index }) => (
                        <td key={`total-${index}`}>
                          {index === 11 ? (datatotal?.qty || 0) : index === 12 ? (
                            // One line per currency when rows are priced in more than one.
                            String(datatotal?.amount || "")
                              .split("+")
                              .map((part) => part.trim())
                              .filter(Boolean)
                              .map((part, partIndex) => <div key={`${part}-${partIndex}`}>{part}</div>)
                          ) : ""}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
                {datalodaer && <TableLoader />}
              </div>
            </div>
            <button className="back_selected_inventory" onClick={() => setPageStatus("print_preview_page")}>Back</button>
            {inventorylist.length > 0 ? (
              <Pagination total={totalPages} current={currentPage} pagination={handlePaginations} />
            ) : ""}
          </div>
        </div>
        <BarcodeModal selectList={selectlist} inventoryList={inventorylist} />
        <ProductModal data_product={getoneproduct} setOneproduct={setOneproduct} editproduct_submit={''} />
        <ProductImageSliderModal main_id={imageid} />
        <Filtermodal submitfiltter={Getmyinventory} limit={limit} custom_filter_enable={true} />
        {renderColFilterMenu()}
        <div className="modal fade common-modal common-modal-danger" id="allocation-listedModal" tabIndex={-1} aria-labelledby="allocation-listedModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                <img className="common-modal-exclamation-img" src={base_assets + "images/icons/exclamation-mark-red.png"} alt="" />
                <p className="modal-content-text">{t("Are you sure you want to Free this item ?")}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn modal-content-no" data-bs-dismiss="modal" onClick={() => deallcocation('NO')}>{t("Order Close")}</button>
                <button type="button" className="btn modal-content-yes" data-bs-dismiss="modal" onClick={() => deallcocation('YES')}>{t("Deallocate")}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <TableColumnModal
        key={JSON.stringify(newcolumndata)}
        tablehead={newcolumndata}
        tblName={'tbl_my_inventory'}
        setnewcolumndata={setnewcolumndata}
        calldatatable={Getmyinventory}
      />
      <ConfirmationModel />
    </React.Fragment>
  );
}

export default InventorySelectToPrintout;
